import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, AlertCircle, CheckCircle, Paperclip } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { getAppUrl } from '../../../lib/url';
import { isValidEmail, readFunctionError } from '../../../lib/email';
import { errorMessage } from '../../../lib/errors';


interface EmailInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerEmail: string;
    customerName: string;
    invoiceNumber: string;
    invoiceId: string;
    orgId: string;
    amountDue: number;
    businessName: string;
    generatePdfBase64: () => Promise<string | null>;
    onSuccess: () => void;
    businessEmail?: string;
    vrm?: string;
    vehicleName?: string;
}

export default function EmailInvoiceModal({
    isOpen,
    onClose,
    customerEmail,
    customerName,
    invoiceNumber,
    invoiceId,
    orgId,
    amountDue,
    businessName,
    generatePdfBase64,
    onSuccess,
    businessEmail,
    vrm,
    vehicleName,
}: EmailInvoiceModalProps) {
    const [emailTo, setEmailTo] = useState(customerEmail);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sendCopy, setSendCopy] = useState(true);
    const [status, setStatus] = useState<'idle' | 'generating' | 'sending' | 'success'>('idle');
    const [error, setError] = useState<string | null>(null);
    const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
    const [stripeVerified, setStripeVerified] = useState<boolean | null>(null);
    const [stripeCheckingStatus, setStripeCheckingStatus] = useState(false);


    // Check Stripe connection and verification status when opening modal
    useEffect(() => {
        if (isOpen && orgId) {
            setStripeCheckingStatus(true);
            supabase
                .from('organizations')
                .select('stripe_account_id, stripe_verification_complete, stripe_charges_enabled')
                .eq('id', orgId)
                .single()
                .then(({ data, error }) => {
                    if (error) {
                        console.error('Failed to check Stripe status:', error);
                        setStripeConnected(false);
                        setStripeVerified(false);
                    } else {
                        setStripeConnected(!!data?.stripe_account_id);
                        setStripeVerified(data?.stripe_verification_complete && data?.stripe_charges_enabled ? true : false);
                    }
                    setStripeCheckingStatus(false);
                });
        }
    }, [isOpen, orgId]);

    // Reset/Populate form when opening
    useEffect(() => {
        if (isOpen) {
            setEmailTo(customerEmail);
            const vehicleText = vrm ? ` regarding ${vehicleName || ''} (${vrm})` : '';
            setSubject(`Invoice ${invoiceNumber} from ${businessName}${vrm ? ` - ${vrm}` : ''}`);
            setMessage(`Hi ${customerName},\n\nYour vehicle${vehicleText} is ready! Please find the invoice attached for £${amountDue.toFixed(2)}.\n\nYou can view the details and pay securely online using the link below:\n\nThank you for choosing ${businessName}!\n\nBest regards,\n${businessName}`);
            setStatus('idle');
            setError(null);
        }
    }, [isOpen, customerEmail, invoiceNumber, businessName, customerName, amountDue, vrm, vehicleName]);

    const handleClose = () => {
        if (status !== 'idle' && status !== 'success') return;
        setError(null);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!emailTo.trim()) {
            setError('Recipient email is required');
            return;
        }
        if (!isValidEmail(emailTo)) {
            setError('That email address looks invalid — check it includes a domain like "name@example.com".');
            return;
        }

        try {
            setStatus('generating');

            // 1. Token-gated payment link (audit finding F-08).
            //
            // This used to be `${getAppUrl()}/pay/invoice/${invoiceId}` — a
            // "stable link that never expires", which is exactly the problem:
            // the invoice UUID was a bearer credential with no expiry and no
            // revocation, so anyone who obtained the URL from a forwarded email
            // or a browser history kept access to the customer's invoice
            // forever. That route no longer exists.
            //
            // fn_issue_invoice_portal_token reuses a live token when one exists,
            // so re-sending an invoice keeps the customer's existing link valid.
            const { data: portalToken, error: tokenError } = await supabase.rpc(
                'fn_issue_invoice_portal_token',
                { p_invoice_id: invoiceId },
            );
            if (tokenError || !portalToken) {
                throw new Error('Could not create a secure payment link. Please try again.');
            }
            const paymentLink = `${getAppUrl()}/pay/t/${portalToken}`;

            // 2. Generate PDF locally
            const pdfBase64 = await generatePdfBase64();
            if (!pdfBase64) {
                throw new Error('Could not generate PDF attachment.');
            }

            setStatus('sending');

            // 3. Call Edge Function with the PDF
            const { error: fnError } = await supabase.functions.invoke('send-email', {
                body: {
                    type: 'invoice_email',
                    app_url: getAppUrl(),
                    data: {
                        invoice_id: invoiceId,
                        org_id: orgId,
                        email: emailTo.trim(),
                        name: customerName,
                        invoice_number: invoiceNumber,
                        amount_due: amountDue,
                        payment_link_url: paymentLink,
                        subject: subject.trim(),
                        message: message.trim(),
                        pdf_base64: pdfBase64,
                        send_copy: sendCopy,
                        business_email: businessEmail
                    }
                }
            });

            if (fnError) throw await readFunctionError(fnError, 'Failed to send email');

            setStatus('success');

            // Allow user to see the success state
            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            console.error('Email error:', err);
            setError(errorMessage(err) || 'Failed to send email');
            setStatus('idle');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <Send className="w-5 h-5 text-accent" />
                        Email Invoice
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg"
                        disabled={status !== 'idle' && status !== 'success'}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Stripe Not Connected Warning */}
                    {!stripeCheckingStatus && !stripeConnected && (
                        <div className="p-4 rounded-lg bg-danger/10 border border-danger/30 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-danger mb-1">Stripe Not Connected</p>
                                <p className="text-xs text-danger mb-3">
                                    Stripe is not connected to your account. The invoice will be sent without a "Pay Now" button. Connect Stripe to enable online payments.
                                </p>
                                <a 
                                    href={`${getAppUrl()}/settings/billing`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-semibold text-danger hover:text-danger px-3 py-1.5 bg-danger/20 rounded hover:bg-danger/30 transition-colors inline-block"
                                >
                                    Connect Stripe Now →
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Stripe Verification Warning (Connected but not verified) */}
                    {!stripeCheckingStatus && stripeConnected && !stripeVerified && (
                        <div className="p-4 rounded-lg bg-gold-fill/10 border border-gold/30 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-gold mb-1">Stripe Verification Pending</p>
                                <p className="text-xs text-gold mb-3">
                                    Your Stripe account is connected but hasn't completed ID verification and KYC checks yet. This email will be sent without a "Pay Now" button. Once verified, customers will be able to pay directly through ForgeDash.
                                </p>
                                <a 
                                    href={`${getAppUrl()}/settings/billing`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-gold hover:text-gold inline-block underline"
                                >
                                    Check Verification Status →
                                </a>
                            </div>
                        </div>
                    )}

                    {/* To Field */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            To <span className="text-danger">*</span>
                        </label>
                        <input
                            type="email"
                            required
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                        />
                    </div>

                    {/* Subject Field */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            Subject <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                        />
                    </div>

                    {/* Message body */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            Message
                        </label>
                        <textarea
                            rows={5}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none disabled:opacity-50"
                        />
                    </div>

                    {/* Attachments Preview */}
                    <div className="flex items-center gap-3 p-3 border border-tint/10 rounded-lg bg-tint/5">
                        <div className="w-10 h-10 bg-danger/20 rounded flex items-center justify-center">
                            <span className="text-danger font-bold text-[10px]">PDF</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-fg truncate">Invoice_{invoiceNumber}.pdf</p>
                            <p className="text-xs text-fg-4 flex items-center gap-1">
                                <Paperclip className="w-3 h-3" /> Attached automatically
                            </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-success" />
                    </div>

                    {/* Options */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="sendCopy"
                            checked={sendCopy}
                            onChange={(e) => setSendCopy(e.target.checked)}
                            disabled={status !== 'idle'}
                            className="rounded border-tint/10 bg-tint/5 text-accent focus:ring-accent disabled:opacity-50"
                        />
                        <label htmlFor="sendCopy" className="text-sm text-fg-3 select-none cursor-pointer">
                            Send a copy to my email {businessEmail && `(${businessEmail})`}
                        </label>
                    </div>

                    {/* Error Display */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-2.5 rounded-lg font-bold text-fg-2 hover:bg-tint/5 border border-tint/10 transition-colors disabled:opacity-50"
                            disabled={status !== 'idle' && status !== 'success'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={status !== 'idle'}
                            className={`flex-1 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 min-w-[120px] ${status === 'success'
                                ? 'bg-success-fill text-on-fill'
                                : 'fd-btn-primary'
                                }`}
                        >
                            {status === 'idle' && (
                                <>
                                    <Send className="w-5 h-5" />
                                    Send Email
                                </>
                            )}
                            {status === 'generating' && (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Generating PDF...
                                </>
                            )}
                            {status === 'sending' && (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Sending...
                                </>
                            )}
                            {status === 'success' && (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    Sent!
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>


        </div>
    );
}
